/**
 * ServiceOrdersDashboardPage - Dashboard do módulo Ordem de Serviço (BR-OS-056)
 *
 * Mesmo padrão do Dashboard de Veículos: cabeçalho com título, atualizar e filtros
 * rápidos; abas via DashboardTabs (permissões em "Permissões Dashboard"); conteúdo via
 * DashboardRenderer. Tempo real único da página invalida o cache dos indicadores.
 *
 * RELEASE: 2026-10-18 v1.0.0 - Criação inicial (aba Visão Geral).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Clock, LayoutDashboard, Loader2, RefreshCw, Wrench } from 'lucide-react';
import { useModuleDashboards } from '@/hooks/useDashboards';
import { DashboardRenderer, DashboardTabs } from '@/components/crm/dashboard';
import { CrmPeriodFilter } from '@/components/crm/filters/CrmPeriodFilter';
import { calculatePeriodDates, type PeriodFilter, type PeriodPreset } from '@/hooks/useCrmOverviewFilters';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PersonSelector } from '@/components/persons/PersonSelector';
import { useServiceTypes } from '@/hooks/useServiceTypes';
import { useTaskCategories } from '@/hooks/useTaskCategories';
import { useRefreshWithTimestamp } from '@/hooks/useRefreshWithTimestamp';
import { useAuth } from '@/hooks/useAuth';
import { useVehiclesModule } from '@/hooks/useVehiclesModule';
import { useStockModule } from '@/hooks/useStockModule';
import { SERVICE_ORDERS_DASHBOARD_QUERY_KEY, type ServiceOrdersDashboardFilters } from '@/hooks/useServiceOrdersDashboardOverview';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';

const FILTER_TRIGGER = 'h-[22px] w-auto min-w-[110px] gap-1 px-2 text-[11px]';

export default function ServiceOrdersDashboardPage() {
  const queryClient = useQueryClient();
  const { currentTenant } = useAuth();
  const { isRefreshing, formattedTime, triggerRefresh } = useRefreshWithTimestamp();
  const { serviceTypes } = useServiceTypes(true);
  // Catálogo oficial de categorias da O.S. (mesmo usado no menu)
  const { categories } = useTaskCategories(true, undefined, true);

  // Filtros rápidos (período padrão: mês atual)
  const [period, setPeriod] = useState<PeriodFilter>(() => ({ preset: 'month', ...calculatePeriodDates('month') }));
  const [serviceTypeId, setServiceTypeId] = useState<string | null>(null);
  const [providerId, setProviderId] = useState<string | null>(null);
  const [categoryId, setCategoryId] = useState<string | null>(null);

  const filters = useMemo<ServiceOrdersDashboardFilters>(() => ({
    startDate: period.startDate, endDate: period.endDate, serviceTypeId, providerId, categoryId,
  }), [period, serviceTypeId, providerId, categoryId]);

  const { dashboards: allDashboards, selectedDashboard: rawSelected, selectedDashboardId: rawSelectedId, selectDashboard, isLoading } = useModuleDashboards('service_orders');
  // BR-OS-059: a aba "Por Veículo" só aparece com o módulo Veículos ativo nas Configurações.
  const { isEnabled: vehiclesEnabled } = useVehiclesModule();
  // BR-OS-060: a aba "Por Movimentação" só aparece com a Gestão Rastreador ativa.
  const { isEnabled: stockEnabled } = useStockModule();
  const dashboards = useMemo(
    () => allDashboards.filter((d) => (vehiclesEnabled || d.component_key !== 'service_orders_vehicles')
      && (stockEnabled || d.component_key !== 'service_orders_movements')),
    [allDashboards, vehiclesEnabled, stockEnabled],
  );
  const selectedDashboard = rawSelected && dashboards.some((d) => d.id === rawSelected.id) ? rawSelected : dashboards[0];
  const selectedDashboardId = selectedDashboard?.id ?? rawSelectedId;

  const invalidate = useCallback(() => queryClient.invalidateQueries({ queryKey: [SERVICE_ORDERS_DASHBOARD_QUERY_KEY] }), [queryClient]);
  const handleRefresh = useCallback(async () => { await triggerRefresh(async () => { await invalidate(); }); }, [triggerRefresh, invalidate]);

  // Tempo real único da página: O.S. e eventos estruturados.
  useEffect(() => {
    if (!currentTenant?.id) return;
    const filter = `tenant_id=eq.${currentTenant.id}`;
    const channel = supabase
      .channel(`service-orders-dashboard-${currentTenant.id}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'service_orders', filter }, () => invalidate())
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'service_order_events', filter }, () => invalidate())
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [currentTenant?.id, invalidate]);

  if (isLoading) {
    return <div className="flex min-h-[60vh] items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>;
  }

  if (dashboards.length === 0) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4">
        <LayoutDashboard className="h-16 w-16 text-muted-foreground/50" />
        <h1 className="text-2xl font-bold text-foreground">Dashboard de O.S.</h1>
        <p className="max-w-md text-center text-muted-foreground">Nenhum dashboard disponível. Verifique suas permissões de acesso em Permissões Dashboard.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex shrink-0 items-center gap-1.5">
          <Wrench className="h-5 w-5 text-primary" />
          <h1 className="text-lg font-bold tracking-tight text-foreground md:text-2xl">Dashboard de O.S.</h1>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleRefresh} disabled={isRefreshing} title="Atualizar">
            <RefreshCw className={cn('h-3.5 w-3.5', isRefreshing && 'animate-spin')} />
          </Button>
          <span className="hidden items-center gap-1 text-[10px] text-muted-foreground md:inline-flex"><Clock className="h-2.5 w-2.5" />{formattedTime}</span>
        </div>
        <span className="shrink-0 text-muted-foreground/30">|</span>
        <div className="flex min-w-0 flex-1 items-center gap-1.5 overflow-x-auto scrollbar-none">
          <CrmPeriodFilter
            className="h-[22px] px-2 text-[11px]"
            value={period}
            onPresetChange={(p: PeriodPreset) => setPeriod({ preset: p, ...calculatePeriodDates(p) })}
            onCustomChange={(s, e) => setPeriod({ preset: 'custom', startDate: s, endDate: e })}
          />
          <Select value={serviceTypeId ?? 'ALL'} onValueChange={(v) => setServiceTypeId(v === 'ALL' ? null : v)}>
            <SelectTrigger className={FILTER_TRIGGER}><SelectValue placeholder="Tipo de serviço" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Todos os tipos</SelectItem>
              {serviceTypes.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={categoryId ?? 'ALL'} onValueChange={(v) => setCategoryId(v === 'ALL' ? null : v)}>
            <SelectTrigger className={FILTER_TRIGGER}><SelectValue placeholder="Categoria" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Todas as categorias</SelectItem>
              {(categories as { id: string; name: string }[]).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <PersonSelector role="service_provider" value={providerId} onSelect={(id) => setProviderId(id)}
            placeholder="Todos os prestadores" noneLabel="Todos os prestadores" className="h-[22px] w-44 shrink-0 text-[11px]" />
        </div>
      </div>

      <DashboardTabs dashboards={dashboards} selectedDashboardId={selectedDashboardId} onSelectDashboard={selectDashboard} alwaysShow />

      {selectedDashboard && (
        <DashboardRenderer componentKey={selectedDashboard.component_key} dashboardId={selectedDashboard.id} serviceOrderFilters={filters} />
      )}
    </div>
  );
}
